import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { Field } from '#app/components/form/field.tsx'
import { PhoneField } from '#app/components/form/phone-field.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import {
	type ContactCandidate,
	type ContactDraft,
	type ContactFormMode,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { cn } from '#app/utils/misc.tsx'

type DraftErrors = Partial<Record<keyof ContactDraft, string>>

const SEARCH_TAB = 'search'
const DETAILS_TAB_MAP: Record<ContactFormMode, string> = {
	add: 'add-details',
	edit: 'edit-details',
}

type Props = {
	readonly formId: string
	readonly mode: ContactFormMode
	readonly draft: ContactDraft
	readonly errors: DraftErrors
	readonly candidates: readonly ContactCandidate[]
	readonly search: string
	readonly selectedCandidateId?: string
	readonly onDraftFieldChange: (
		field: keyof ContactDraft,
		value: string,
	) => void
	readonly onSearchChange: (value: string) => void
	readonly onCandidateSelect: (candidate: ContactCandidate) => void
	readonly onCandidateClear: () => void
	readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void
	readonly excludedEmails?: readonly string[]
	readonly currentEmail?: string
	readonly isOpen?: boolean
	readonly onTabChange?: (tab: 'details' | 'new' | 'search') => void
}

export function ContactForm({
	formId,
	mode,
	draft,
	errors,
	candidates,
	search,
	selectedCandidateId,
	onDraftFieldChange,
	onSearchChange,
	onCandidateSelect,
	onCandidateClear,
	onSubmit,
	excludedEmails,
	currentEmail,
	isOpen,
	onTabChange,
}: Props) {
	const overlayOpen = isOpen ?? true
	const detailsTab = DETAILS_TAB_MAP[mode]
	const NEW_TAB = 'new-details'
	const [activeTab, setActiveTabState] = useState<string>(() => {
		if (mode === 'edit') return detailsTab
		return candidates.length > 0 ? SEARCH_TAB : detailsTab
	})

	const emitTabChange = useCallback(
		(next: string) => {
			setActiveTabState((current) => (current === next ? current : next))
			if (next === detailsTab) {
				onTabChange?.('details')
				return
			}
			if (next === NEW_TAB) {
				onTabChange?.('new')
				return
			}
			if (next === SEARCH_TAB) {
				onTabChange?.('search')
			}
		},
		[detailsTab, NEW_TAB, onTabChange],
	)

	useEffect(() => {
		setActiveTabState((current) => {
			if (mode === 'edit') return detailsTab
			if (candidates.length === 0 && current === SEARCH_TAB) {
				return detailsTab
			}
			return current
		})
	}, [mode, detailsTab, candidates.length])

	const normalizedCurrentEmail = useMemo(() => {
		const value = currentEmail ?? ''
		return value.trim().toLowerCase()
	}, [currentEmail])

	const excludedEmailSet = useMemo(() => {
		const set = new Set<string>()
		for (const entry of excludedEmails ?? []) {
			if (!entry) continue
			const normalized = entry.trim().toLowerCase()
			if (!normalized) continue
			if (normalizedCurrentEmail && normalized === normalizedCurrentEmail)
				continue
			set.add(normalized)
		}
		return set
	}, [excludedEmails, normalizedCurrentEmail])

	const availableCandidates = useMemo(() => {
		if (!candidates.length) return [] as ContactCandidate[]
		return candidates.filter((candidate) => {
			const normalizedEmail = candidate.email.trim().toLowerCase()
			if (!normalizedEmail) return true
			return !excludedEmailSet.has(normalizedEmail)
		})
	}, [candidates, excludedEmailSet])

	const filteredCandidates = useMemo(() => {
		if (!search.trim()) return availableCandidates
		const term = search.trim().toLowerCase()
		return availableCandidates.filter((candidate) => {
			return [candidate.name, candidate.email, candidate.phone]
				.filter(Boolean)
				.some((value) => value?.toLowerCase().includes(term))
		})
	}, [availableCandidates, search])

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		onSearchChange(event.target.value)
	}

	const handleDraftInputChange =
		(field: keyof ContactDraft) => (event: ChangeEvent<HTMLInputElement>) => {
			onDraftFieldChange(field, event.target.value)
		}

	const hasAnyCandidates = candidates.length > 0
	const hasVisibleCandidates = availableCandidates.length > 0
	const noCandidatesDueToExclusion = !hasVisibleCandidates && hasAnyCandidates
	const showSearchTab = hasVisibleCandidates || noCandidatesDueToExclusion
	const shouldShowNoMatches =
		hasVisibleCandidates && filteredCandidates.length === 0

	const nameAutoFocus = activeTab === detailsTab || activeTab === NEW_TAB

	const initialSearchSyncedRef = useRef<string | null>(null)

	useEffect(() => {
		if (!overlayOpen) {
			initialSearchSyncedRef.current = null
			return
		}
		if (mode !== 'edit') return
		if (!selectedCandidateId) {
			initialSearchSyncedRef.current = null
			return
		}
		if (initialSearchSyncedRef.current === selectedCandidateId) return
		if (!showSearchTab) return
		const hasCandidate = availableCandidates.some(
			(candidate) => candidate.id === selectedCandidateId,
		)
		if (!hasCandidate) return
		initialSearchSyncedRef.current = selectedCandidateId
		emitTabChange(SEARCH_TAB)
	}, [
		overlayOpen,
		mode,
		selectedCandidateId,
		availableCandidates,
		showSearchTab,
		emitTabChange,
	])

	const handleCandidateClick = (candidate: ContactCandidate) => {
		if (selectedCandidateId === candidate.id) {
			onCandidateClear()
			emitTabChange(detailsTab)
			return
		}
		onCandidateSelect(candidate)
	}

	return (
		<form id={formId} className="space-y-4" onSubmit={onSubmit}>
			<Tabs value={activeTab} onValueChange={emitTabChange}>
				<TabsList className="space-x-6">
					<TabsTrigger value={detailsTab}>
						{mode === 'add' ? 'Add' : 'Edit'}
					</TabsTrigger>
					{mode === 'edit' ? (
						<TabsTrigger value={NEW_TAB}>New</TabsTrigger>
					) : null}
					{showSearchTab ? (
						<TabsTrigger value={SEARCH_TAB}>Search</TabsTrigger>
					) : null}
				</TabsList>
				{showSearchTab ? (
					<TabsContent value={SEARCH_TAB}>
						<div className="space-y-4">
							<input
								type="search"
								id={`${formId}-search-input`}
								value={search}
								onChange={handleSearchChange}
								placeholder="Search by name, email, or phone"
								className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
							/>
							<div className="max-h-56 space-y-2 overflow-y-auto pr-1">
								{filteredCandidates.length > 0 ? (
									filteredCandidates.map((candidate) => {
										const selected = selectedCandidateId === candidate.id
										const displayName =
											candidate.name.trim().length > 0
												? candidate.name
												: 'Unnamed contact'
										return (
											<ContactCard
												key={candidate.id}
												name={displayName}
												title={candidate.source ?? ''}
												email={candidate.email}
												phone={candidate.phone ?? undefined}
												onClick={() => handleCandidateClick(candidate)}
												isSelected={selected}
												showRemoveAction={false}
												className={cn(
													'border-border bg-background focus-visible:ring-primary/60 focus-visible:ring-offset-background border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
													selected
														? 'border-primary bg-neutral-100'
														: 'hover:border-primary/60 hover:bg-neutral-100',
												)}
											/>
										)
									})
								) : shouldShowNoMatches ? (
									<p className="text-muted-foreground text-sm">
										No matching contacts found. Try refining your search or add
										a new person.
									</p>
								) : noCandidatesDueToExclusion ? (
									<p className="text-muted-foreground text-sm">
										Everyone from your saved contacts is already in this list.
										Add a new person or edit an existing entry instead.
									</p>
								) : (
									<p className="text-muted-foreground text-sm">
										You haven&apos;t saved any contacts yet. Add one using the
										form.
									</p>
								)}
							</div>
						</div>
					</TabsContent>
				) : null}
				{mode === 'edit' ? (
					<TabsContent value={NEW_TAB}>
						<div className="pt-2">
							<Field
								labelProps={{
									children: "What is the contact's name?",
								}}
								inputProps={{
									value: draft.name,
									onChange: handleDraftInputChange('name'),
									type: 'text',
									autoFocus: activeTab === NEW_TAB,
								}}
								errors={errors.name ? [errors.name] : undefined}
							/>
							<Field
								labelProps={{
									children: 'What is the job title of this contact?',
								}}
								inputProps={{
									value: draft.title,
									onChange: handleDraftInputChange('title'),
									type: 'text',
								}}
								errors={errors.title ? [errors.title] : undefined}
							/>
							<Field
								labelProps={{
									children: 'What email address can we reach this contact at?',
								}}
								inputProps={{
									value: draft.email,
									onChange: handleDraftInputChange('email'),
									type: 'email',
								}}
								errors={errors.email ? [errors.email] : undefined}
							/>
							<PhoneField
								labelProps={{
									children: 'What phone number can we call? (optional)',
								}}
								inputProps={{
									value: draft.phone,
									onChange: handleDraftInputChange('phone'),
									type: 'phone',
									name: 'phone',
								}}
							/>
						</div>
					</TabsContent>
				) : null}
				<TabsContent value={detailsTab}>
					<div className="pt-2">
						<Field
							labelProps={{
								children: "What is the contact's name?",
							}}
							inputProps={{
								value: draft.name,
								onChange: handleDraftInputChange('name'),
								type: 'text',
								autoFocus: nameAutoFocus,
							}}
							errors={errors.name ? [errors.name] : undefined}
						/>
						<Field
							labelProps={{
								children: 'What is the job title of this contact?',
							}}
							inputProps={{
								value: draft.title,
								onChange: handleDraftInputChange('title'),
								type: 'text',
							}}
							errors={errors.title ? [errors.title] : undefined}
						/>
						<Field
							labelProps={{
								children: 'What email address can we reach this contact at?',
							}}
							inputProps={{
								value: draft.email,
								onChange: handleDraftInputChange('email'),
								type: 'email',
							}}
							errors={errors.email ? [errors.email] : undefined}
						/>
						<PhoneField
							labelProps={{
								children: 'What phone number can we reach this contact at?',
							}}
							inputProps={{
								value: draft.phone,
								onChange: handleDraftInputChange('phone'),
								name: `${formId}-phone`,
							}}
						/>
					</div>
				</TabsContent>
			</Tabs>
		</form>
	)
}
